/**
 * Video ↔ Prompt Enhancement (bound GenerateText) contract.
 *
 * Typed FIELD_BINDING round-trip (NOT execution dependency):
 *   GenerateVideo.promptText (simple/original)
 *     → GenerateText primary input / scoped_context.original_prompt
 *     → GenerateText.currentResult
 *     → GenerateVideo.enhancedPromptText
 *
 * Reuses generate_text + text.generation providers/skills/billing.
 */

import type { InputAssetRef } from './videoKeyframeTimeline'
import { hasAnyMention, parsePromptText } from './assetMention'

/** VistoeraTASK-00064 P8 — `fields.promptText` may carry invisible
 * U+2063 Asset Mention markers (`assetMention.ts`) once Problem 1's
 * inline asset references are in use. The Prompt Enhancement Skill only
 * ever receives natural-language `original_prompt` — a raw marker
 * leaking through would show the LLM literal control-character noise
 * (or, worse, a stray token it might echo back mangled) instead of the
 * intended `[人物缩略图]`-style bracket text (spec §1/§8 "不能...把 token
 * 变成 `[object Object]`"). Resolved using EACH mention's own persisted
 * `aliasHint` (the label captured at the moment it was dropped) — never
 * a live alias-map lookup here: this is a fire-and-forget context
 * snapshot handed to a stateless Skill, not the real Generate submit
 * path (which already re-resolves against the LIVE map via
 * `resolvePromptForSubmit` in `GenerateVideoNode.tsx`, so a renamed/
 * disconnected asset is still handled correctly there). */
export function resolvePromptTextForEnhancementContext(text: string): string {
  if (!text || !hasAnyMention(text)) return text
  return parsePromptText(text)
    .map((seg) => (seg.type === 'text' ? seg.value : `[${seg.aliasHint || seg.edgeId}]`))
    .join('')
}

export const VIDEO_PROMPT_ENHANCEMENT_PURPOSE = 'video_prompt_enhancement' as const
export const VIDEO_PROMPT_ENHANCEMENT_SKILL_KEY = 'video_prompt_enhancement'
export const VIDEO_PROMPT_ENHANCEMENT_SKILL_VERSION = 'v1'
/** Capability taxonomy remains text.generation; skill is provider-neutral. */
export const VIDEO_PROMPT_ENHANCEMENT_CAPABILITY = 'text.generation'

/** Canvas edge relationTypes — field binding, never execution DAG edges. */
export const PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION = 'PROMPT_ENHANCEMENT_SOURCE'
export const PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION = 'PROMPT_ENHANCEMENT_RESULT'

/** Canonical React Flow handle ids for the prompt-enhancement round-trip. */
export const VIDEO_PROMPT_SOURCE_OUT_HANDLE = 'original-prompt-out'
export const VIDEO_PROMPT_CONTEXT_IN_HANDLE = 'prompt-context-in'

/** Default GenerateText additional_instruction for enhancer workflow. */
export const VIDEO_PROMPT_ENHANCEMENT_DEFAULT_INSTRUCTION =
  '请根据上面的提示词优化成导演提示词。'

export type EffectivePromptSource = 'ORIGINAL' | 'ENHANCED'
export type EnhancedPromptBindingMode = 'AUTO_BOUND' | 'MANUAL_OVERRIDE'

export interface VideoPromptEnhancementContextSnapshot {
  purpose: typeof VIDEO_PROMPT_ENHANCEMENT_PURPOSE
  original_prompt: string
  source_video_node_id: string
  source_field: 'promptText'
  binding_type: 'video_prompt_enhancement_input'
  keyframes: Array<Record<string, unknown>>
  characters: Array<Record<string, unknown>>
  products: Array<Record<string, unknown>>
  scenes: Array<Record<string, unknown>>
  other_reference_assets: Array<Record<string, unknown>>
  reference_videos: Array<Record<string, unknown>>
  duration: number | null
  aspect_ratio: string | null
  resolution: string | null
  audio_settings: Record<string, unknown> | null
}

export function isVideoPromptEnhancementPurpose(purpose: unknown): boolean {
  return String(purpose || '').trim() === VIDEO_PROMPT_ENHANCEMENT_PURPOSE
}

export function isPromptEnhancementFieldBindingRelation(relation: unknown): boolean {
  const r = String(relation || '').trim()
  return r === PROMPT_ENHANCEMENT_SOURCE_EDGE_RELATION || r === PROMPT_ENHANCEMENT_RESULT_EDGE_RELATION
}

/** Model / skill refusal phrases must never become enhanced_prompt. */
export function looksLikePromptEnhancementRefusal(text: unknown): boolean {
  const t = String(text || '').trim()
  if (!t) return true
  const markers = [
    '未提供原始视频提示词',
    '无法生成增强后的视频提示词',
    '缺少必要输入',
    'missing required input',
    'original prompt was not provided',
    'no original prompt',
  ]
  return markers.some((m) => t.includes(m))
}

export function resolveEffectivePromptSource(fields: Record<string, unknown>): EffectivePromptSource {
  const explicit = String(fields.effectivePromptSource || '').trim().toUpperCase()
  const enhanced = String(fields.enhancedPromptText || '').trim()
  if (explicit === 'ENHANCED' && enhanced) return 'ENHANCED'
  if (explicit === 'ORIGINAL') return 'ORIGINAL'
  if (enhanced) return 'ENHANCED'
  return 'ORIGINAL'
}

export function resolveEffectiveVideoPrompt(fields: Record<string, unknown>): {
  effectivePrompt: string
  effectivePromptSource: EffectivePromptSource
  originalPrompt: string
  enhancedPrompt: string
} {
  const originalPrompt = String(fields.promptText || '').trim()
  const enhancedPrompt = String(fields.enhancedPromptText || '').trim()
  const effectivePromptSource = resolveEffectivePromptSource(fields)
  const effectivePrompt =
    effectivePromptSource === 'ENHANCED' && enhancedPrompt ? enhancedPrompt : originalPrompt
  return { effectivePrompt, effectivePromptSource, originalPrompt, enhancedPrompt }
}

/** Build structured context from Video node fields — not an untracked string dump. */
export function buildVideoPromptEnhancementContextSnapshot(args: {
  videoNodeId: string
  fields: Record<string, unknown>
}): VideoPromptEnhancementContextSnapshot {
  const fields = args.fields
  const original = resolvePromptTextForEnhancementContext(String(fields.promptText || '')).trim()
  const timeline = Array.isArray(fields.keyframeTimeline) ? fields.keyframeTimeline : []
  const keyframes = timeline.map((entry) => {
    if (!entry || typeof entry !== 'object') return {}
    const e = entry as Record<string, unknown>
    return {
      id: e.id ?? null,
      role: e.role ?? null,
      visualDescription: e.visualDescription ?? null,
      motionIntent: e.motionIntent ?? null,
      cameraIntent: e.cameraIntent ?? null,
      relativeTimeSec: e.relativeTimeSec ?? null,
    }
  })

  const refs = Array.isArray(fields.inputAssetRefs) ? (fields.inputAssetRefs as InputAssetRef[]) : []
  const characters: Array<Record<string, unknown>> = []
  const products: Array<Record<string, unknown>> = []
  const scenes: Array<Record<string, unknown>> = []
  const other_reference_assets: Array<Record<string, unknown>> = []
  const reference_videos: Array<Record<string, unknown>> = []

  for (const ref of refs) {
    if (!ref || typeof ref !== 'object') continue
    const row = {
      id: ref.id ?? null,
      label: ref.label ?? null,
      category: ref.category ?? null,
      assetId: ref.assetId ?? null,
      entityRefId: ref.entityRefId ?? null,
    }
    const cat = String(ref.category || '').toLowerCase()
    if (cat.includes('character') || cat.includes('model') || cat.includes('person')) {
      characters.push(row)
    } else if (cat.includes('product') || cat.includes('object')) {
      products.push(row)
    } else if (cat.includes('scene') || cat.includes('background') || cat.includes('location')) {
      scenes.push(row)
    } else if (cat.includes('video')) {
      reference_videos.push(row)
    } else {
      other_reference_assets.push(row)
    }
  }

  const durationRaw = fields.durationSeconds
  const duration = typeof durationRaw === 'number' && Number.isFinite(durationRaw) ? durationRaw : null
  const aspect =
    (typeof fields.ratio === 'string' && fields.ratio) ||
    (typeof (fields.lockedProductionFormat as { aspect_ratio?: string } | null)?.aspect_ratio === 'string'
      ? (fields.lockedProductionFormat as { aspect_ratio: string }).aspect_ratio
      : null)
  const resolution =
    (typeof fields.qualityTier === 'string' && fields.qualityTier) ||
    (typeof (fields.lockedProductionFormat as { resolution?: string } | null)?.resolution === 'string'
      ? (fields.lockedProductionFormat as { resolution: string }).resolution
      : null)

  return {
    purpose: VIDEO_PROMPT_ENHANCEMENT_PURPOSE,
    original_prompt: original,
    source_video_node_id: args.videoNodeId,
    source_field: 'promptText',
    binding_type: 'video_prompt_enhancement_input',
    keyframes,
    characters,
    products,
    scenes,
    other_reference_assets,
    reference_videos,
    duration,
    aspect_ratio: aspect,
    resolution,
    audio_settings: Array.isArray(fields.dialogueSegments)
      ? { dialogue_segment_count: fields.dialogueSegments.length }
      : null,
  }
}

export function buildVideoPromptEnhancementWritebackPatch(args: {
  enhancedPrompt: string
  runId: string | null
  skillKey: string | null
  skillVersion: string | null
  textProviderId?: string | null
  /** When MANUAL_OVERRIDE, do not overwrite unless forceApply. */
  currentBindingMode?: EnhancedPromptBindingMode | string | null
  forceApply?: boolean
}): Record<string, unknown> {
  const text = String(args.enhancedPrompt || '').trim()
  if (!text) return {}
  if (looksLikePromptEnhancementRefusal(text)) return {}
  if (args.currentBindingMode === 'MANUAL_OVERRIDE' && !args.forceApply) return {}
  return {
    enhancedPromptText: text,
    effectivePromptSource: 'ENHANCED' as EffectivePromptSource,
    enhancedPromptBindingMode: 'AUTO_BOUND' as EnhancedPromptBindingMode,
    promptEnhancementRunId: args.runId,
    promptEnhancementSkillId: args.skillKey,
    promptEnhancementSkillVersion: args.skillVersion,
    promptEnhancementProviderId: args.textProviderId ?? null,
    promptEnhancementUpdatedAt: new Date().toISOString(),
    promptEnhancementSourceField: 'generate_text.currentResult',
    promptEnhancementBindingType: 'PROMPT_ENHANCEMENT',
  }
}

export function clearEnhancedPromptPatch(): Record<string, unknown> {
  return {
    enhancedPromptText: '',
    effectivePromptSource: 'ORIGINAL' as EffectivePromptSource,
    enhancedPromptBindingMode: 'AUTO_BOUND' as EnhancedPromptBindingMode,
    promptEnhancementRunId: null,
    promptEnhancementSkillId: null,
    promptEnhancementSkillVersion: null,
    promptEnhancementProviderId: null,
  }
}

export function markEnhancedPromptManualOverride(): Record<string, unknown> {
  return {
    enhancedPromptBindingMode: 'MANUAL_OVERRIDE' as EnhancedPromptBindingMode,
  }
}
